/**
 * Task-force self-claim pool — `/tasks?t=<member-token>`  (PUBLIC, no Cognito).
 *
 * An internal operational tool for the human task force, reached only via a
 * personal tokenized link emailed from the admin Task Force page. Like the
 * admin panel, this page is English-only and intentionally NOT wired into the
 * 40-locale i18n machinery — the audience is a small internal team, not site
 * visitors.
 *
 * Members see the available pool + their own claimed tasks, claim (atomic — a
 * 409 means someone else grabbed it first), mark in-progress, and mark published
 * with the live URL (which closes the loop for the backlink check).
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPool, claimTask, updateTask, type PoolTask } from '../../api/taskForce';

const STATUS_PILL: Record<string, string> = {
  available: 'bg-blue-100 text-blue-700',
  claimed: 'bg-amber-100 text-amber-700',
  'in-progress': 'bg-violet-100 text-violet-700',
  published: 'bg-green-100 text-green-700',
  verified: 'bg-emerald-100 text-emerald-700',
};
const pill = (s: string) => STATUS_PILL[s] ?? 'bg-gray-100 text-gray-500';

export function TaskForcePool() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tf-pool', token],
    queryFn: () => getPool(token),
    enabled: !!token,
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['tf-pool', token] });

  if (!token) {
    return <Shell><Msg>Missing or invalid link. Please use the personal link from your task email.</Msg></Shell>;
  }
  if (isLoading) return <Shell><Msg>Loading your tasks…</Msg></Shell>;
  if (error) {
    return <Shell><Msg>This link is invalid or has expired — ask an admin to resend it.</Msg></Shell>;
  }

  const available = data?.available ?? [];
  const mine = data?.mine ?? [];

  return (
    <Shell>
      <h1 className="text-2xl font-semibold text-gray-900">Hi {data?.member.name} 👋</h1>
      <p className="mt-1 text-sm text-gray-500">
        Claim a task, publish it, then paste the live URL to mark it done. Thanks for helping spread the word!
      </p>

      <Section title={`Your tasks (${mine.length})`}>
        {mine.length === 0 && <Empty>Nothing claimed yet — grab one from the pool below.</Empty>}
        {mine.map((t) => <MyCard key={t.taskId} task={t} token={token} onDone={invalidate} />)}
      </Section>

      <Section title={`Available to claim (${available.length})`}>
        {available.length === 0 && <Empty>The pool is empty right now. Check back soon!</Empty>}
        {available.map((t) => <PoolCard key={t.taskId} task={t} token={token} onDone={invalidate} />)}
      </Section>
    </Shell>
  );
}

function PoolCard({ task, token, onDone }: { task: PoolTask; token: string; onDone: () => void }) {
  const claim = useMutation({ mutationFn: () => claimTask(token, task.taskId), onSettled: onDone });
  return (
    <Card>
      <CardHead task={task} />
      {task.payload && <p className="mt-1 text-xs text-gray-500">{task.payload}</p>}
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={() => claim.mutate()}
          disabled={claim.isPending}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {claim.isPending ? 'Claiming…' : 'Claim'}
        </button>
        {claim.error && <span className="text-xs text-amber-600">Someone grabbed it — refreshing.</span>}
      </div>
    </Card>
  );
}

function MyCard({ task, token, onDone }: { task: PoolTask; token: string; onDone: () => void }) {
  const [url, setUrl] = useState(task.publishedUrl ?? '');
  const mut = useMutation({
    mutationFn: (v: { status: 'in-progress' | 'published' | 'released'; publishedUrl?: string }) =>
      updateTask(token, task.taskId, v.status, v.publishedUrl),
    onSettled: onDone,
  });
  const done = task.status === 'published' || task.status === 'verified';

  return (
    <Card>
      <CardHead task={task} />
      {task.payload && <p className="mt-1 text-xs text-gray-500">{task.payload}</p>}

      {!done && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {task.status === 'claimed' && (
            <button onClick={() => mut.mutate({ status: 'in-progress' })} disabled={mut.isPending}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Start
            </button>
          )}
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… published URL"
            className="min-w-0 flex-1 rounded border border-gray-200 px-2 py-1.5 text-sm"
          />
          <button
            onClick={() => mut.mutate({ status: 'published', publishedUrl: url.trim() })}
            disabled={mut.isPending || !/^https?:\/\//i.test(url.trim())}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            Mark published
          </button>
          <button onClick={() => mut.mutate({ status: 'released' })} disabled={mut.isPending}
            className="rounded-md px-2 py-1.5 text-xs text-gray-400 hover:text-red-600">
            Release
          </button>
        </div>
      )}
      {done && task.publishedUrl && (
        <p className="mt-2 text-xs text-gray-500">
          Published: <a href={task.publishedUrl} target="_blank" rel="noreferrer" className="break-all text-primary-700 hover:underline">{task.publishedUrl}</a>
          {task.backlinkStatus ? ` · backlink: ${task.backlinkStatus}` : ''}
        </p>
      )}
      {mut.error && <p className="mt-2 text-xs text-amber-600">{String((mut.error as Error)?.message)}</p>}
    </Card>
  );
}

// ─── Presentational bits ────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">{children}</div>
    </div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">{children}</div>;
}
function CardHead({ task }: { task: PoolTask }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="font-medium text-gray-900">{task.title}</div>
        <div className="text-xs text-gray-400">{task.type}{task.platform ? ` · ${task.platform}` : ''}</div>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${pill(task.status)}`}>{task.status}</span>
    </div>
  );
}
function Msg({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">{children}</div>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-center text-sm text-gray-400">{children}</p>;
}
